// Punches erase strokes and user-drawn walls into one material layer's
// detected-mask image, producing exactly what the backend expects: an
// opaque black background with white wall pixels. Runs identically inside
// the mask-render Worker (on an OffscreenCanvas) and the main-thread
// fallback (on a regular <canvas>) — the 2D context API is the same shape
// either way, and `layerImage` can be an ImageBitmap or an
// HTMLImageElement (drawImage accepts both).
//
// Erase strokes must be punched out (destination-out) on a transparent
// canvas BEFORE the opaque black background is composited underneath —
// filling black first would let destination-out cut through the background
// too, leaving transparent pixels instead of the solid black the backend
// treats as "no wall here".
export function renderMaskLayer(
  canvas,
  {
    layerImage,
    eraseStrokes,
    wallElements,
    activeMaterialKey,
    layerKey,
    scaleX,
    scaleY,
    lineScale,
    exportWidth,
    exportHeight,
  },
) {
  canvas.width = exportWidth;
  canvas.height = exportHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = false;

  if (layerImage) ctx.drawImage(layerImage, 0, 0, exportWidth, exportHeight);

  for (const stroke of eraseStrokes) {
    const pts = stroke.points || [];
    if (pts.length < 4) continue;
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    ctx.strokeStyle = "rgba(0,0,0,1)";
    ctx.lineWidth = stroke.width * lineScale;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(pts[0] * scaleX, pts[1] * scaleY);
    for (let i = 2; i < pts.length; i += 2) {
      ctx.lineTo(pts[i] * scaleX, pts[i + 1] * scaleY);
    }
    ctx.stroke();
    ctx.restore();
  }

  for (const wall of wallElements) {
    const wallLayerKey = wall.materialKey || activeMaterialKey;
    ctx.save();
    ctx.globalCompositeOperation =
      wallLayerKey === layerKey ? "source-over" : "destination-out";
    ctx.strokeStyle = "rgba(255,255,255,1)";
    ctx.lineWidth = wall.thickness * lineScale;
    ctx.lineCap = "square";
    ctx.beginPath();
    ctx.moveTo(wall.points[0].x * scaleX, wall.points[0].y * scaleY);
    ctx.lineTo(wall.points[1].x * scaleX, wall.points[1].y * scaleY);
    ctx.stroke();
    ctx.restore();
  }

  const flattened = newCanvasLike(canvas, exportWidth, exportHeight);
  const flatCtx = flattened.getContext("2d");
  if (!flatCtx) return canvas;
  flatCtx.fillStyle = "#000000";
  flatCtx.fillRect(0, 0, exportWidth, exportHeight);
  flatCtx.drawImage(canvas, 0, 0);
  return flattened;
}

function newCanvasLike(canvas, width, height) {
  if (typeof OffscreenCanvas !== "undefined" && canvas instanceof OffscreenCanvas) {
    return new OffscreenCanvas(width, height);
  }
  const el = document.createElement("canvas");
  el.width = width;
  el.height = height;
  return el;
}

// OffscreenCanvas (available inside Workers) has no toDataURL — encode via
// convertToBlob + FileReader instead, stripped to bare base64 exactly like
// the regular-canvas path so the payload shape is identical either way.
export async function canvasToBase64(canvas) {
  if (typeof canvas.convertToBlob === "function") {
    const blob = await canvas.convertToBlob({ type: "image/png" });
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
    return dataUrl.replace(/^data:image\/\w+;base64,/, "");
  }
  return canvas.toDataURL("image/png").replace(/^data:image\/\w+;base64,/, "");
}
